import { shallowRef, readonly } from 'vue';
import { connect, disconnect, getAccount, reconnect, switchChain, watchAccount } from '@wagmi/core';
import { config } from './walletConfig';
import { POLYGON_CHAIN_ID } from './polygonRpc';
import { setWalletRecoveryState } from './walletRecovery';

const account = shallowRef(getAccount(config));
let epoch = 0, activeEpoch = 0, blocked = false;
watchAccount(config, { onChange: value => {
  if (!blocked && activeEpoch === epoch && value.status !== 'reconnecting') account.value = value;
} });
let connecting, restoring, connectingEpoch, restoringEpoch;
let queue = Promise.resolve();
function serialize(operation) {
  const result = queue.then(operation, operation);
  queue = result.catch(() => {});
  return result;
}
const pause = () => new Promise(resolve => setTimeout(resolve, 100));
async function findConnector(version) {
  // Async injection can happen after initial page scripts or app hydration.
  for (let attempt = 0; attempt < 50 && version === epoch; attempt++) {
    const available = [];
    for (const connector of config.connectors) {
      let provider;
      try { provider = await connector.getProvider(); } catch { continue; }
      if (provider) available.push({ connector, provider });
    }
    const selected = available.find(item => item.provider.isRiceApp) || available[0];
    if (selected) return selected.connector;
    await pause();
  }
  return null;
}
export function useWalletAccount() { return readonly(account); }
export function connectNativeWallet() {
  if (connecting && connectingEpoch === epoch) return connecting;
  const version = ++epoch;
  blocked = false;
  setWalletRecoveryState('idle');
  connectingEpoch = version;
  const operation = serialize(async () => {
    if (version !== epoch) return getAccount(config);
    activeEpoch = version;
    if (!getAccount(config).isConnected) {
      const connector = await findConnector(version);
      if (version !== epoch) return getAccount(config);
      if (!connector) throw new Error('請使用 RiceApp 或錢包內建瀏覽器開啟此網站。');
      await connect(config, { connector });
    }
    if (version !== epoch) { await disconnect(config); return getAccount(config); }
    if (getAccount(config).chainId !== POLYGON_CHAIN_ID)
      await switchChain(config, { chainId: POLYGON_CHAIN_ID });
    if (version !== epoch) { await disconnect(config); return getAccount(config); }
    if (getAccount(config).chainId !== POLYGON_CHAIN_ID) throw new Error('請切換至 Polygon 網路。');
    account.value = getAccount(config);
    return account.value;
  });
  const tracked = operation.finally(() => { if (connecting === tracked) connecting = null; });
  connecting = tracked;
  return tracked;
}
// A current eth_accounts result is required; no stored address is a connection.
export function restoreNativeWallet() {
  if (blocked || (connecting && connectingEpoch === epoch)) return Promise.resolve([]);
  if (restoring && restoringEpoch === epoch) return restoring;
  const version = epoch;
  setWalletRecoveryState('restoring');
  restoringEpoch = version;
  const operation = serialize(async () => {
    if (blocked || version !== epoch) return [];
    activeEpoch = version;
    const connector = await findConnector(version);
    if (!connector || blocked || version !== epoch) return [];
    const result = await reconnect(config, { connectors: [connector] });
    if (blocked || version !== epoch) { await disconnect(config); return []; }
    account.value = getAccount(config);
    return result;
  });
  const tracked = operation.finally(() => {
    if (version === epoch) setWalletRecoveryState('idle');
    if (restoring === tracked) restoring = null;
  });
  restoring = tracked;
  return tracked;
}
export function disconnectNativeWallet() {
  ++epoch;
  blocked = true;
  setWalletRecoveryState('idle');
  // Hide an obsolete account immediately, even if a provider read is still pending.
  account.value = { status: 'disconnected', isConnected: false, address: undefined, chainId: undefined };
  return serialize(async () => {
    await disconnect(config);
    activeEpoch = epoch;
    if (blocked) account.value = getAccount(config);
  });
}
